import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ptKanjiMeanings from "./locales/pt/kanjiMeanings.json";

type KanjiMeaningEntry = { symbol: string; meaning: string };

export type LocalizedKanjiEntry<T extends KanjiMeaningEntry> = T & { englishMeaning: string };

const ptMeanings = ptKanjiMeanings as Record<string, string>;

export function kanjiLanguage(language: string): "pt" | "en" {
    return language === "pt" ? "pt" : "en";
}

export function localizeKanjiMeaning(symbol: string, englishMeaning: string, language: string): string {
    return kanjiLanguage(language) === "pt" ? ptMeanings[symbol] ?? englishMeaning : englishMeaning;
}

export function localizeKanjiList<T extends KanjiMeaningEntry>(list: T[], language: string): LocalizedKanjiEntry<T>[] {
    return list.map((entry) => ({
        ...entry,
        englishMeaning: entry.meaning,
        meaning: localizeKanjiMeaning(entry.symbol, entry.meaning, language),
    }));
}

export function useKanjiLanguage(): "pt" | "en" {
    const { i18n } = useTranslation();
    return kanjiLanguage(i18n.language);
}

export function useLocalizedKanjiList<T extends KanjiMeaningEntry>(list: T[]): LocalizedKanjiEntry<T>[] {
    const language = useKanjiLanguage();
    return useMemo(() => localizeKanjiList(list, language), [list, language]);
}

export function useKanjiMeaning(): (symbol: string, englishMeaning: string) => string {
    const language = useKanjiLanguage();
    return useCallback(
        (symbol: string, englishMeaning: string) => localizeKanjiMeaning(symbol, englishMeaning, language),
        [language]
    );
}
